import {HttpHeaders} from '@angular/common/http'
import {preventBgoProprietaryHttpInterceptorHeader} from '../http/prevent.interceptor'
import {ApolloLink} from '@apollo/client/core'
import {HttpLink} from 'apollo-angular/http'
import {ConfigService} from '../config/config.service'
import {Kind, OperationTypeNode, print, stripIgnoredCharacters} from 'graphql'
import {CMS_GRAPHQL_CLIENT_NAME} from '../content/content-graphql-split'
import {setContext} from '@apollo/client/link/context'
import {Injectable, OnDestroy} from '@angular/core'
import {getMainDefinition} from '@apollo/client/utilities'
import {Logger} from '../logging/logger.types'
import {I18nService} from '../../craft/i18n/i18n.service'
import {GraphqlWebsocketStatusService} from './graphql-websocket-status.service'
import {Client} from 'graphql-ws'
import {onError} from '@apollo/client/link/error'
import {hasGraphqlAuthenticationError, hasGraphqlForbiddenError} from './graphql-utils'
import {LinkService} from '../i18n/link.service'
import {RetryLink} from '@apollo/client/link/retry'
import {take} from 'rxjs/operators'
import {from, Observable} from 'rxjs'
import {REDIRECT_TO_PARAMETER, SIGN_OUT_PARAMETER} from '../../pages/public/auth/sign-in/sign-in.types'
import {PUBLIC_ROUTES} from '../../shared/routing/public.routes'

@Injectable({providedIn: 'root'})
export class GraphqlLinkFactory implements OnDestroy {
  private wsClient: Client | undefined

  constructor(
    private readonly websocketService: GraphqlWebsocketStatusService,
    private readonly configService: ConfigService,
    private readonly httpLink: HttpLink,
    private readonly logger: Logger,
    private readonly i18n: I18nService,
    private readonly linkService: LinkService,
  ) {}

  ngOnDestroy(): void {
    this.closeWebSocketConnection()
  }

  build(getJwtToken?: () => Promise<string | undefined>): ApolloLink {
    const authLink = this.authLink(getJwtToken)
    const retryAuthErrorLink = this.retryAuthError(getJwtToken)
    const authErrorLink = this.authErrorLink(getJwtToken)
    return this._build({authLink, retryAuthErrorLink, authErrorLink})
  }

  async buildWithPubSub(getJwtToken?: () => Promise<string | undefined>): Promise<ApolloLink> {
    const authLink = this.authLink(getJwtToken)
    const pubsubLink = await this.pubSubApiLink(getJwtToken)
    const retryAuthErrorLink = this.retryAuthError(getJwtToken)
    const authErrorLink = this.authErrorLink(getJwtToken)
    return this._build({authLink, pubsubLink, retryAuthErrorLink, authErrorLink})
  }

  private _build({
    authLink,
    pubsubLink,
    retryAuthErrorLink,
    authErrorLink,
  }: {
    authLink?: ApolloLink
    pubsubLink?: ApolloLink
    retryAuthErrorLink?: ApolloLink
    authErrorLink?: ApolloLink
  }): ApolloLink {
    const datoCmsApi = this.datoCmsApiLink()
    const coreApi = this.coreGraphqlApiLink({authLink, retryAuthErrorLink, authErrorLink})

    // the pubsub API with real time capabilities is separated from the core API
    // split traffic to our backend API between the pubsub endpoint (with websockets) and the core api
    const buildigoApi = pubsubLink
      ? ApolloLink.split(
          ({query}) => {
            const definition = getMainDefinition(query)
            return (
              definition.kind === Kind.OPERATION_DEFINITION && definition.operation === OperationTypeNode.SUBSCRIPTION
            )
          },
          pubsubLink,
          coreApi,
        )
      : coreApi

    // split traffic between CMS and our backend API depending on the client name
    return ApolloLink.split(
      op => [CMS_GRAPHQL_CLIENT_NAME].includes(op.getContext().clientName),
      datoCmsApi,
      buildigoApi,
    )
  }

  private authLink(getJwtToken?: () => Promise<string | undefined>) {
    if (!getJwtToken) {
      this.logger.warn('getJwtToken is not set')
      return undefined
    }
    return setContext(() => {
      this.logger.debug('authLink(): Adding auth headers')
      return withAuthHeaders(getJwtToken)
    })
  }

  private coreGraphqlApiLink({
    authLink,
    retryAuthErrorLink,
    authErrorLink,
  }: {
    authLink?: ApolloLink
    retryAuthErrorLink?: ApolloLink
    authErrorLink?: ApolloLink
  }): ApolloLink {
    const buildigoApi = this.httpLink.create({
      uri: this.configService.config.apiUrl,
      headers: new HttpHeaders({
        // manually set these headers for this link, because other links may not accept these headers in their CORS policy
        'apollographql-client-name': 'bgo-web-ui',
        'apollographql-client-version': this.configService.config.version,
      }),
      // override default behavior (print) to strip out unnecessary characters, makes queries smaller
      operationPrinter: documentNode => stripIgnoredCharacters(print(documentNode)),
    })

    /**
     * ApolloLink[] = [
     *   authErrorLink,         // logs the user out if auth fails
     *   retryAuthErrorLink,    // refreshes & replays if that token is bad
     *   authLink,              // adds the current token
     *   httpLink,              // does the actual request
     * ];
     *
     * RESPONSE:
     * httpLink (3) → authLink (2) → retryAuthErrorLink (1) → authErrorLink (0)
     **/

    const links: ApolloLink[] = []
    if (authErrorLink) {
      links.push(authErrorLink)
    }
    if (retryAuthErrorLink) {
      links.push(retryAuthErrorLink)
    }
    if (authLink) {
      links.push(authLink)
    }
    links.push(buildigoApi)
    return ApolloLink.from(links)
  }

  private async pubSubApiLink(getJwtToken?: () => Promise<string | undefined>) {
    if (!getJwtToken) {
      this.logger.warn('getJwtToken is not set')
      return undefined
    }

    const pubSubApiUrl = this.configService.config.pubSubApiUrl
    if (!pubSubApiUrl) {
      this.logger.warn(`No pubsub config found!`)
      return undefined
    }

    // Close existing WebSocket connection if any
    this.closeWebSocketConnection()

    const {GraphQLWsLink} = await import('@apollo/client/link/subscriptions')
    const {createClient} = await import('graphql-ws')

    this.wsClient = createClient({
      url: pubSubApiUrl,
      shouldRetry: () => true,
      // Use a function to get fresh tokens on each connection/reconnection
      connectionParams: async () => {
        try {
          const authToken = await getJwtToken()
          if (!authToken) {
            // Don't attempt to connect without a valid token
            throw new Error('Unable to get authentication token for WebSocket connection')
          }
          return {
            authToken,
            language: this.i18n.getActiveLang(),
          }
        } catch (err) {
          this.logger.error('Failed to get auth token for WebSocket', err)
          // Re-throw to prevent connection attempt with invalid credentials
          throw new Error('WebSocket authentication failed')
        }
      },
      on: {
        connecting: () => {
          this.logger.debug('GraphQLWsLink:connecting')
          this.websocketService._updateStatus('connecting')
        },
        connected: () => {
          this.logger.debug('GraphQLWsLink:connected')
          this.websocketService._updateStatus('connected')
        },
        closed: () => {
          this.logger.debug('GraphQLWsLink:closed')
          this.websocketService._updateStatus('disconnected')
        },
        error: (error: Error) => {
          this.logger.debug('GraphQLWsLink:error', error)
          this.websocketService._updateStatus('disconnected')
        },
      },
    })

    return new GraphQLWsLink(this.wsClient)
  }

  closeWebSocketConnection(): void {
    if (this.wsClient) {
      this.logger.debug('Closing existing WebSocket connection')
      this.wsClient.dispose()
      this.wsClient = undefined
    }
  }

  private retryAuthError(getJwtToken?: () => Promise<string | undefined>) {
    return new RetryLink({
      attempts: {
        retryIf: error => {
          if (getJwtToken && hasGraphqlAuthenticationError(error)) {
            this.logger.debug('Retrying authentication error with updated bearer token')
            return true
          }
          return false
        },
        max: 2,
      },
    })
  }

  private authErrorLink(getJwtToken?: () => Promise<string | undefined>) {
    if (!getJwtToken) return undefined

    return onError(({graphQLErrors, networkError}) => {
      const hasAuthError =
        graphQLErrors?.some(e => e.extensions?.code === 'UNAUTHENTICATED') ||
        hasGraphqlAuthenticationError(networkError) ||
        hasGraphqlForbiddenError(networkError)

      if (hasAuthError) {
        this.logger.debug(`authErrorLink logging out because of authentication error`, {networkError})
        logout(this.linkService).pipe(take(1)).subscribe()
      }
    })
  }

  private datoCmsApiLink() {
    const {apiToken, preview, environment} = this.configService.config.datocms
    return this.httpLink.create({
      uri: `https://graphql.datocms.com/`,
      headers: new HttpHeaders({
        ...preventBgoProprietaryHttpInterceptorHeader,
        Authorization: `Bearer ${apiToken}`,
        ...(preview ? {'X-Include-Drafts': 'true'} : undefined),
        ...(environment ? {'X-Environment': environment} : undefined),
      }),
      // override default behavior (print) to strip out unnecessary characters, makes queries smaller
      // this helps with APIs query size limit
      // https://www.contentful.com/developers/docs/references/graphql/#/introduction/query-size-limits
      // https://www.apollographql.com/docs/react/networking/advanced-http-networking/
      operationPrinter: documentNode => stripIgnoredCharacters(print(documentNode)),
    })
  }
}

export async function withAuthHeaders(getJwtToken: () => Promise<string | undefined>) {
  const bearerToken = await getJwtToken()
  if (bearerToken) {
    return {headers: {Authorization: 'Bearer ' + bearerToken}}
  } else {
    return {}
  }
}

function logout(linkService: LinkService, redirectTo?: string | null): Observable<unknown> {
  const queryParams: Record<string, string> = {[SIGN_OUT_PARAMETER]: 'true'}
  let route = PUBLIC_ROUTES.SIGN_IN
  if (redirectTo) {
    route = PUBLIC_ROUTES.SIGN_IN_RETURNING
    queryParams[REDIRECT_TO_PARAMETER] = redirectTo
  }
  return from(linkService.navigateTo(route, {queryParams}))
}
